import type { ItemLogUsage } from '@/types/itemLogUsage'

export type DateTimeInput = string | Date | null | undefined

export function formatDateTime(
  value: DateTimeInput,
  locale = 'en-GB',
  options?: Intl.DateTimeFormatOptions,
  fallback = '-',
) {
  if (!value) return fallback

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return fallback

  return date.toLocaleString(locale, options)
}

type DurationLabels = {
  hour?: string
  minute?: string
}

const defaultDurationLabels = {
  hour: 'hr.',
  minute: 'min.',
}

function formatDuration(totalMs: number, labels: DurationLabels = {}) {
  const minutes = Math.floor(totalMs / (1000 * 60))
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  const durationLabels = { ...defaultDurationLabels, ...labels }

  return `${hours} ${durationLabels.hour} ${remainingMinutes} ${durationLabels.minute}`
}

export function getUsageDuration(log: ItemLogUsage, labels?: DurationLabels) {
  const startValue = log.startAt ?? log.startDate
  if (!startValue) return '-'

  const start = new Date(startValue).getTime()
  const endValue = log.endAt ?? log.endDate
  const end = endValue ? new Date(endValue).getTime() : Date.now()

  if (Number.isNaN(start) || Number.isNaN(end)) return '-'

  return formatDuration(end - start, labels)
}

export function getTotalUsageDuration(logs: ItemLogUsage[], labels?: DurationLabels) {
  const totalMs = logs.reduce((sum, log) => {
    const startValue = log.startAt ?? log.startDate
    if (!startValue) return sum

    const start = new Date(startValue).getTime()
    const endValue = log.endAt ?? log.endDate
    const end = endValue ? new Date(endValue).getTime() : Date.now()
    if (Number.isNaN(start) || Number.isNaN(end)) return sum

    return sum + (end - start)
  }, 0)

  return formatDuration(totalMs, labels)
}

export function getLocalDateString(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export const toUtcDateString = (value?: string | null) => {
  if (!value) return null

  // Already a datetime string
  if (value.includes('T')) {
    return value
  }

  return `${value}T00:00:00Z`
}
